import { Link } from "react-router-dom";
import { useLocale, useT, EN_AVAILABLE_ROUTES } from "../lib/i18n";
import commonFr from "../locales/fr/common.json";
import commonEn from "../locales/en/common.json";

// External sites in the Bruno Courtin ecosystem — order kept stable. Locale-agnostic.
const EXTERNAL_SITES = [
  { key: "istas",           label: "ISTAS",             url: "https://istas.fr" },
  { key: "human-knowledge", label: "Human Knowledge",   url: "https://humanknowledge.fr" },
  { key: "nextcap",         label: "NEXTCAP",           url: "https://nextcap-pro.com" },
  { key: "way-of-dojo",     label: "Way of Dojo",       url: "https://wayofdojo.fr" },
];

// Nav destinations per locale. Slugs of proper nouns unchanged.
const NAV = {
  fr: [
    { key: "nav.author",     to: "/auteur" },
    { key: "nav.novels",     to: "/romans" },
    { key: "nav.cinema",     to: "/cinema/protocole-zero" },
    { key: "nav.news",       to: "/actualites" },
    { key: "nav.climate",    to: "/journal-climat" },
    { key: "nav.press",      to: "/presse" },
    { key: "nav.publishers", to: "/editeurs-agents" },
    { key: "nav.contact",    to: "/contact" },
  ],
  en: [
    { key: "nav.author",     to: "/en/author" },
    { key: "nav.novels",     to: "/en/novels" },
    { key: "nav.cinema",     to: "/en/film/protocol-zero" },
    { key: "nav.news",       to: "/en/news" },
    { key: "nav.climate",    to: "/en/climate-journal" },
    { key: "nav.press",      to: "/en/press" },
    { key: "nav.publishers", to: "/en/publishers-agents" },
    { key: "nav.contact",    to: "/en/contact" },
  ],
};

const INFO = {
  fr: [
    { key: "footer.ecosystem", to: "/ecosysteme" },
    { key: "footer.legal",     to: "/mentions-legales" },
    { key: "footer.privacy",   to: "/confidentialite" },
    { key: "footer.cookies",   to: "/cookies" },
  ],
  en: [
    { key: "footer.ecosystem", to: "/en/ecosystem" },
    { key: "footer.legal",     to: "/en/legal-notice" },
    { key: "footer.privacy",   to: "/en/privacy-policy" },
    { key: "footer.cookies",   to: "/en/cookies" },
  ],
};

export default function Footer() {
  const { locale } = useLocale();
  const t = useT({ fr: commonFr, en: commonEn });
  // Prune EN nav / info links to routes that actually exist today. Entries pointing
  // to a route not yet in EN_AVAILABLE_ROUTES are hidden (masked) rather than
  // rendered as broken/silent-redirect links — see i18n.js allowlist.
  const nav = locale === "en"
    ? NAV.en.filter((l) => EN_AVAILABLE_ROUTES.has(l.to))
    : NAV.fr;
  const info = locale === "en"
    ? INFO.en.filter((l) => EN_AVAILABLE_ROUTES.has(l.to))
    : INFO.fr;

  return (
    <footer
      className="theme-ink px-6 lg:px-12 pt-24 pb-0"
      data-testid="site-footer"
      data-locale={locale}
    >
      <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>{t("footer.official")}</p>
          <h2 className="font-display text-4xl md:text-5xl mt-3 mb-6">Bruno Courtin</h2>
          <p className="max-w-md text-[15px] leading-relaxed opacity-80">
            {t("footer.tagline")}
          </p>
        </div>

        <div>
          <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>{t("footer.navigation")}</p>
          <ul className="mt-4 space-y-2 text-[14px]">
            {nav.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="nav-link"
                  data-testid={`footer-nav-${l.key.replace(/\W+/g, "-")}`}
                >
                  {t(l.key)}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>{t("footer.information")}</p>
          <ul className="mt-4 space-y-2 text-[14px]">
            {info.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  className="nav-link"
                  data-testid={`footer-info-${l.key.replace(/\W+/g, "-")}`}
                >
                  {t(l.key)}
                </Link>
              </li>
            ))}
            {/* "Administration" link intentionally removed from public footer (LOT 3B).
                 The /admin/login route stays functional and can be accessed directly. */}
          </ul>
        </div>

        <div data-testid="footer-nos-sites">
          <p className="eyebrow" style={{ color: "var(--gold-soft)" }}>{t("footer.our_sites")}</p>
          <ul className="mt-4 space-y-2 text-[14px]">
            <li>
              <span className="opacity-60 cursor-default" data-testid="footer-site-brunocourtin">
                {locale === "en" ? "Bruno Courtin — Author" : "Bruno Courtin — Auteur"}
              </span>
            </li>
            {EXTERNAL_SITES.map((s) => (
              <li key={s.key}>
                {/* eslint-disable-next-line react/jsx-no-target-blank */}
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener"
                  data-testid={`footer-site-${s.key}`}
                  className="nav-link"
                  aria-label={locale === "en" ? `${s.label} (opens in a new tab)` : `${s.label} (nouvelle fenêtre)`}
                >
                  {s.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="max-w-[1440px] mx-auto mt-16">
        <div className="rule-hair" aria-hidden="true" />
        <div className="py-6 md:py-8 flex flex-col md:flex-row items-center justify-between gap-3 md:gap-6">
          <p className="text-[12px] font-mono opacity-70 leading-none">
            © {new Date().getFullYear()} Bruno Courtin — {t("footer.rights")}
          </p>
          <p className="text-[12px] font-mono opacity-70 leading-none">brunocourtin.fr</p>
        </div>
      </div>
    </footer>
  );
}
